// Translations for the Accounting / ERP module (Accounting.js): journals,
// invoices, customers, suppliers, banking, budgets, reports, settings.
// Reuses shared terms already defined in dict/shell.js (Customers, Suppliers,
// Accounts Receivable/Payable, Receipts, Payments, Banking & Cash, General
// Ledger, Budgets, Financial Reports, Accounting Settings, Reconciliation,
// Statement Import, Cash / Bank Book, Accounting Overview, User) instead of
// redefining them here.
const accountingDict = [
  // Dialog / prompt strings (some authored in Swahili originally)
  { en: "Reason for rejecting the journal:", sw: "Sababu ya kukataa journal:" },
  { en: "Reason for reversal:", sw: "Sababu ya reversal:" },
  { en: "Reason for rejecting the receipt:", sw: "Sababu ya kukataa receipt:" },
  { en: "Reason for rejection:", sw: "Sababu ya kukataa:" },
  { en: "Journal entry ID to match:", sw: "Namba ya jarida la kuoanisha:" },
  { en: "Resolution notes:", sw: "Maelezo ya utatuzi:" },
  { en: "Reason for rejecting the transfer:", sw: "Sababu ya kukataa transfer:" },
  { en: "All members already have a customer record (none new).", sw: "Wanachama wote tayari wana rekodi ya customer (hakuna mpya)." },

  // Customer/member sync banner
  { en: "Members are the customers.", sw: "Wanachama ndio wateja." },
  { en: "Members who don't yet have a customer record can be added with one click instead of being re-typed one by one.", sw: "Wanachama ambao bado hawana rekodi ya mteja wanaweza kuongezwa kwa kubofya kitufe kimoja badala ya kuandikwa upya mmoja mmoja." },
  { en: "Syncing...", sw: "Inasawazisha..." },
  { en: "Sync members → customers", sw: "Sawazisha wanachama → wateja" },

  // Overview / navigation
  { en: "Financial workspace", sw: "Nafasi ya kazi ya fedha" },
  { en: "Accounting modules", sw: "Moduli za uhasibu" },
  { en: "Overview", sw: "Muhtasari" },
  { en: "Receivables", sw: "Madai" },
  { en: "Payables", sw: "Madeni" },
  { en: "Purchase Orders", sw: "Oda za Ununuzi" },
  { en: "Fixed Assets", sw: "Mali za Kudumu" },
  { en: "Budget Overview", sw: "Muhtasari wa Bajeti" },
  { en: "Create Budget", sw: "Tengeneza Bajeti" },
  { en: "Import Budget", sw: "Ingiza Bajeti" },
  { en: "Your finance workspace at a glance", sw: "Muhtasari wa haraka wa fedha zako" },
  { en: "Debtor master records and account contacts", sw: "Kumbukumbu za wadaiwa na anwani za mawasiliano" },
  { en: "Creditor master records and payment details", sw: "Kumbukumbu za wadai na maelezo ya malipo" },
  { en: "Customer invoices, debtors and outstanding income", sw: "Ankara za wateja, wadaiwa na mapato yanayodaiwa" },
  { en: "Record and manage money received", sw: "Rekodi na simamia fedha zilizopokelewa" },
  { en: "Supplier invoices, creditors and amounts due", sw: "Ankara za wauzaji, wadai na kiasi kinachodaiwa" },
  { en: "Payment requests and outgoing funds", sw: "Maombi ya malipo na fedha zinazotoka" },
  { en: "Bank accounts, statements and reconciliation", sw: "Akaunti za benki, taarifa na upatanisho" },
  { en: "Double-entry journals and posting controls", sw: "Majarida ya kuingiza mara mbili na udhibiti wa uwekaji" },
  { en: "Plan, approve and monitor expenditure", sw: "Panga, idhinisha na fuatilia matumizi" },
  { en: "Trial balance and management reporting", sw: "Muhtasari wa mizani na taarifa za uongozi" },
  { en: "Chart of accounts and financial years", sw: "Orodha ya akaunti na miaka ya fedha" },
  { en: "Approvals", sw: "Idhini" },
  { en: "Budget vs Actual", sw: "Bajeti dhidi ya Uhalisia" },
  { en: "Trial Balance", sw: "Mizani ya Majaribio" },
  { en: "Income & Expenditure", sw: "Mapato na Matumizi" },
  { en: "Financial Position", sw: "Hali ya Kifedha" },
  { en: "Cash & Bank", sw: "Fedha Taslimu na Benki" },
  { en: "AR Aging", sw: "Uchakavu wa Madeni ya Wateja" },
  { en: "AP Aging", sw: "Uchakavu wa Madeni ya Wauzaji" },
  { en: "Transactions", sw: "Miamala" },
  { en: "Audit", sw: "Ukaguzi" },
  { en: "Accounts", sw: "Akaunti" },
  { en: "Transfers", sw: "Uhamisho" },
  { en: "Chart of Accounts", sw: "Orodha ya Akaunti" },
  { en: "Cost Centres", sw: "Vituo vya Gharama" },
  { en: "Financial Years", sw: "Miaka ya Fedha" },
  { en: "Approval Hierarchy", sw: "Mfumo wa Idhini" },
  { en: "Loading accounting data…", sw: "Inapakia takwimu za uhasibu…" },

  // Overview KPIs / workspaces
  { en: "Money owed to you", sw: "Fedha unazodaiwa" },
  { en: "Money you owe", sw: "Fedha unazodaiwa (wewe unadaiwa)" },
  { en: "Bank & cash accounts", sw: "Akaunti za benki na fedha taslimu" },
  { en: "Journals awaiting action", sw: "Majarida yanayosubiri hatua" },
  { en: "Sales invoices, receipts and debtor balances", sw: "Ankara za mauzo, risiti na mizani ya wadaiwa" },
  { en: "Supplier invoices, payments and creditor balances", sw: "Ankara za wauzaji, malipo na mizani ya wadai" },
  { en: "Banking", sw: "Benki" },
  { en: "Statements, cash accounts and reconciliation", sw: "Taarifa za benki, akaunti za fedha na upatanisho" },
  { en: "Journals, chart of accounts and posting", sw: "Majarida, orodha ya akaunti na uwekaji" },
  { en: "Approved limits and actual expenditure", sw: "Mipaka iliyoidhinishwa na matumizi halisi" },
  { en: "Trial balance and finance reporting", sw: "Mizani ya majaribio na taarifa za fedha" },

  // General ledger / journals
  { en: "New journal", sw: "Jarida jipya" },
  { en: "Financial year", sw: "Mwaka wa fedha" },
  { en: "Reference", sw: "Kumbukumbu" },
  { en: "Description", sw: "Maelezo" },
  { en: "Account", sw: "Akaunti" },
  { en: "National / no branch", sw: "Kitaifa / hakuna tawi" },
  { en: "No cost centre", sw: "Hakuna kituo cha gharama" },
  { en: "Line description", sw: "Maelezo ya mstari" },
  { en: "Debit", sw: "Debiti" },
  { en: "Credit", sw: "Kredit" },
  { en: "Add line", sw: "Ongeza mstari" },
  { en: "Save draft", sw: "Hifadhi rasimu" },
  { en: "Number", sw: "Namba" },
  { en: "Date", sw: "Tarehe" },
  { en: "Status", sw: "Hali" },
  { en: "Actions", sw: "Vitendo" },
  { en: "Documents", sw: "Nyaraka" },
  { en: "Submit", sw: "Wasilisha" },
  { en: "Approve", sw: "Idhinisha" },
  { en: "Reject", sw: "Kataa" },
  { en: "Post", sw: "Chapisha" },
  { en: "Reverse", sw: "Tengua" },

  // Receipts / payments
  { en: "Receipt", sw: "Risiti" },
  { en: "Payer", sw: "Mlipaji" },
  { en: "Amount", sw: "Kiasi" },
  { en: "Method", sw: "Njia" },
  { en: "Return to draft", sw: "Rudisha kwenye rasimu" },
  { en: "Print", sw: "Chapisha" },
  { en: "Payment", sw: "Malipo" },
  { en: "Payee", sw: "Mlipwaji" },
  { en: "Approval step", sw: "Hatua ya idhini" },
  { en: "Approve current step", sw: "Idhinisha hatua ya sasa" },
  { en: "Confirm paid", sw: "Thibitisha imelipwa" },
  { en: "Voucher", sw: "Hati ya malipo" },

  // Customers / suppliers
  { en: "Organization", sw: "Taasisi" },
  { en: "Individual", sw: "Mtu binafsi" },
  { en: "Member", sw: "Mwanachama" },
  { en: "Select member…", sw: "Chagua mwanachama…" },
  { en: "already linked", sw: "tayari ameunganishwa" },
  { en: "Customer name", sw: "Jina la mteja" },
  { en: "Email", sw: "Barua pepe" },
  { en: "Phone", sw: "Simu" },
  { en: "Address", sw: "Anwani" },
  { en: "TIN / Registration number", sw: "Namba ya TIN / Usajili" },
  { en: "Add customer", sw: "Ongeza mteja" },
  { en: "Customer", sw: "Mteja" },
  { en: "Type", sw: "Aina" },
  { en: "Supplier name", sw: "Jina la muuzaji" },
  { en: "Bank name", sw: "Jina la benki" },
  { en: "Bank account", sw: "Akaunti ya benki" },
  { en: "Add supplier", sw: "Ongeza muuzaji" },
  { en: "Supplier", sw: "Muuzaji" },
  { en: "Bank", sw: "Benki" },
  { en: "Customer invoices", sw: "Ankara za wateja" },
  { en: "Supplier invoices", sw: "Ankara za wauzaji" },

  // Receivables / payables
  { en: "Track amounts owed to TAREWU.", sw: "Fuatilia kiasi kinachodaiwa TAREWU." },
  { en: "Track amounts TAREWU owes suppliers.", sw: "Fuatilia kiasi TAREWU inachodaiwa wauzaji." },
  { en: "View receipts", sw: "Angalia risiti" },
  { en: "View payments", sw: "Angalia malipo" },
  { en: "Select customer", sw: "Chagua mteja" },
  { en: "Select supplier", sw: "Chagua muuzaji" },
  { en: "Control account", sw: "Akaunti ya udhibiti" },
  { en: "Income account", sw: "Akaunti ya mapato" },
  { en: "Expense account", sw: "Akaunti ya matumizi" },
  { en: "Create invoice", sw: "Tengeneza ankara" },
  { en: "Invoice", sw: "Ankara" },
  { en: "Due", sw: "Tarehe ya mwisho" },
  { en: "Outstanding", sw: "Kiasi kinachodaiwa" },
  { en: "View", sw: "Angalia" },

  // Budgets
  { en: "Total budgets", sw: "Jumla ya bajeti" },
  { en: "Draft", sw: "Rasimu" },
  { en: "Awaiting approval", sw: "Inasubiri idhini" },
  { en: "Approved", sw: "Imeidhinishwa" },
  { en: "Budget register", sw: "Orodha ya bajeti" },
  { en: "Budgets grouped by financial year and approval status.", sw: "Bajeti zilizopangwa kwa mwaka wa fedha na hali ya idhini." },
  { en: "Create budget", sw: "Tengeneza bajeti" },
  { en: "Budget", sw: "Bajeti" },
  { en: "Financial Year", sw: "Mwaka wa Fedha" },
  { en: "Create a budget", sw: "Tengeneza bajeti mpya" },
  { en: "Set an expenditure limit against an expense account.", sw: "Weka kikomo cha matumizi dhidi ya akaunti ya matumizi." },
  { en: "Budget name", sw: "Jina la bajeti" },
  { en: "National budget", sw: "Bajeti ya kitaifa" },
  { en: "All cost centres", sw: "Vituo vyote vya gharama" },
  { en: "Approved amount", sw: "Kiasi kilichoidhinishwa" },
  { en: "Budget line description", sw: "Maelezo ya mstari wa bajeti" },
  { en: "Save draft budget", sw: "Hifadhi rasimu ya bajeti" },
  { en: "You do not have permission to create budgets.", sw: "Huna ruhusa ya kutengeneza bajeti." },
  { en: "Budget approvals", sw: "Idhini za bajeti" },
  { en: "Review budgets submitted for authorization.", sw: "Kagua bajeti zilizowasilishwa kwa idhini." },
  { en: "Decision", sw: "Uamuzi" },
  { en: "Import budgets from CSV", sw: "Ingiza bajeti kutoka CSV" },
  { en: "Download the sample, retain its column names, then validate the file before importing.", sw: "Pakua mfano, tunza majina ya safu wima, kisha thibitisha faili kabla ya kuingiza." },
  { en: "Download CSV sample", sw: "Pakua mfano wa CSV" },
  { en: "Required CSV format", sw: "Muundo wa CSV unaohitajika" },
  { en: "must match an existing financial-year name.", sw: "lazima ilingane na jina la mwaka wa fedha uliopo." },
  { en: "must be an active expense account.", sw: "lazima iwe akaunti ya matumizi inayotumika." },
  { en: "Leave", sw: "Acha" },
  { en: "blank for a national budget.", sw: "wazi kwa bajeti ya kitaifa." },
  { en: "Choose completed CSV file", sw: "Chagua faili la CSV lililojazwa" },
  { en: "Validating…", sw: "Inathibitisha…" },
  { en: "Validate & preview", sw: "Thibitisha na hakiki" },
  { en: "File is ready to import", sw: "Faili liko tayari kuingizwa" },
  { en: "Correct the errors before importing", sw: "Rekebisha makosa kabla ya kuingiza" },
  { en: "budgets", sw: "bajeti" },
  { en: "lines", sw: "mistari" },
  { en: "Branch", sw: "Tawi" },
  { en: "National", sw: "Kitaifa" },
  { en: "Importing…", sw: "Inaingiza…" },
  { en: "Confirm import", sw: "Thibitisha uingizaji" },
  { en: "BUDGET PERFORMANCE", sw: "UTENDAJI WA BAJETI" },
  { en: "Actual expenditure comes from posted General Ledger entries.", sw: "Matumizi halisi yanatokana na maingizo yaliyochapishwa ya Daftari Kuu." },
  { en: "Expense Account", sw: "Akaunti ya Matumizi" },
  { en: "Actual", sw: "Halisi" },
  { en: "Committed", sw: "Iliyoahidiwa" },
  { en: "Available", sw: "Inayopatikana" },
  { en: "Utilization", sw: "Kiwango cha Matumizi" },

  // Banking
  { en: "Account name", sw: "Jina la akaunti" },
  { en: "Cash", sw: "Fedha taslimu" },
  { en: "Institution", sw: "Taasisi" },
  { en: "Account number", sw: "Namba ya akaunti" },
  { en: "Asset ledger account", sw: "Akaunti ya rasilimali ya daftari" },
  { en: "Opening balance", sw: "Salio la ufunguzi" },
  { en: "Add account", sw: "Ongeza akaunti" },
  { en: "Kind", sw: "Aina" },
  { en: "Currency", sw: "Sarafu" },
  { en: "Opening", sw: "Ufunguzi" },
  { en: "From account", sw: "Kutoka akaunti" },
  { en: "To account", sw: "Kwenda akaunti" },
  { en: "Create transfer", sw: "Tengeneza uhamisho" },
  { en: "Transfer", sw: "Uhamisho" },
  { en: "From", sw: "Kutoka" },
  { en: "To", sw: "Kwenda" },
  { en: "Choose account", sw: "Chagua akaunti" },
  { en: "View book", sw: "Angalia kitabu" },
  { en: "Closing balance", sw: "Salio la mwisho" },
  { en: "Journal", sw: "Jarida" },
  { en: "Money In", sw: "Fedha Zilizoingia" },
  { en: "Money Out", sw: "Fedha Zilizotoka" },
  { en: "Balance", sw: "Salio" },
  { en: "Import bank statement", sw: "Ingiza taarifa ya benki" },
  { en: "Download the sample, validate the CSV, then confirm the import.", sw: "Pakua mfano, thibitisha CSV, kisha thibitisha uingizaji." },
  { en: "Statement CSV", sw: "CSV ya taarifa ya benki" },
  { en: "transactions", sw: "miamala" },
  { en: "Direction", sw: "Mwelekeo" },
  { en: "Add a single statement row", sw: "Ongeza mstari mmoja wa taarifa" },
  { en: "Money in", sw: "Fedha zinazoingia" },
  { en: "Money out", sw: "Fedha zinazotoka" },
  { en: "Add row", sw: "Ongeza mstari" },
  { en: "Period", sw: "Kipindi" },
  { en: "Statement balance", sw: "Salio la taarifa" },
  { en: "Statement closing balance", sw: "Salio la mwisho la taarifa" },
  { en: "Prepare reconciliation", sw: "Andaa upatanisho" },
  { en: "Review items", sw: "Kagua vipengele" },
  { en: "Reconciliation items", sw: "Vipengele vya upatanisho" },
  { en: "Close", sw: "Funga" },
  { en: "Match journal", sw: "Oanisha na jarida" },
  { en: "Resolve difference", sw: "Suluhisha tofauti" },

  // Settings
  { en: "Code", sw: "Msimbo" },
  { en: "No parent", sw: "Hakuna mzazi" },
  { en: "Save changes", sw: "Hifadhi mabadiliko" },
  { en: "Cancel", sw: "Ghairi" },
  { en: "Edit", sw: "Hariri" },
  { en: "Deactivate", sw: "Zima" },
  { en: "Activate", sw: "Washa" },
  { en: "Cost Centre", sw: "Kituo cha Gharama" },
  { en: "Cost centre name", sw: "Jina la kituo cha gharama" },
  { en: "Name e.g. 2026", sw: "Jina mf. 2026" },
  { en: "Add year", sw: "Ongeza mwaka" },
  { en: "Name", sw: "Jina" },
  { en: "Start", sw: "Mwanzo" },
  { en: "End", sw: "Mwisho" },
  { en: "Close year", sw: "Funga mwaka" },
  { en: "Reopen year", sw: "Fungua mwaka tena" },
  { en: "Payment approvals are selected automatically by amount. Steps must be completed in order.", sw: "Idhini za malipo huchaguliwa kiotomatiki kwa kiasi. Hatua lazima zikamilishwe kwa mpangilio." },
  { en: "Policy name", sw: "Jina la sera" },
  { en: "Minimum amount", sw: "Kiasi cha chini" },
  { en: "Maximum amount (blank = no limit)", sw: "Kiasi cha juu (wazi = hakuna kikomo)" },
  { en: "Approval steps", sw: "Hatua za idhini" },
  { en: "Approver role", sw: "Wajibu wa muidhinishaji" },
  { en: "Add step", sw: "Ongeza hatua" },
  { en: "Save hierarchy", sw: "Hifadhi mfumo" },
  { en: "Policy", sw: "Sera" },
  { en: "Transaction", sw: "Muamala" },
  { en: "Minimum", sw: "Kiwango cha chini" },
  { en: "Maximum", sw: "Kiwango cha juu" },
  { en: "Approval flow", sw: "Mtiririko wa idhini" },
  { en: "No limit", sw: "Hakuna kikomo" },

  // Reports
  { en: "Print / Save PDF", sw: "Chapisha / Hifadhi PDF" },
  { en: "Export CSV", sw: "Hamisha CSV" },
  { en: "Preparing financial report…", sw: "Inaandaa ripoti ya fedha…" },
  { en: "Balances calculated from posted double-entry journals only.", sw: "Mizani inayohesabiwa kutoka majarida yaliyochapishwa pekee." },
  { en: "Posted transactions from", sw: "Miamala iliyochapishwa kuanzia" },
  { en: "to", sw: "hadi" },
  { en: "Operating performance from", sw: "Utendaji wa uendeshaji kuanzia" },
  { en: "Total Income", sw: "Jumla ya Mapato" },
  { en: "Total Expenditure", sw: "Jumla ya Matumizi" },
  { en: "Surplus / Deficit", sw: "Ziada / Pungufu" },
  { en: "Statement of Financial Position", sw: "Taarifa ya Hali ya Kifedha" },
  { en: "Assets, liabilities and accumulated funds as at", sw: "Rasilimali, madeni na fedha zilizokusanywa kufikia" },
  { en: "Total Assets", sw: "Jumla ya Rasilimali" },
  { en: "Total Liabilities", sw: "Jumla ya Madeni" },
  { en: "Accumulated Funds", sw: "Fedha Zilizokusanywa" },
  { en: "Cash & Bank Balances", sw: "Salio la Fedha Taslimu na Benki" },
  { en: "Balances linked to posted General Ledger movements.", sw: "Mizani inayohusiana na miamala iliyochapishwa ya Daftari Kuu." },
  { en: "Ledger", sw: "Daftari" },
  { en: "Accounts Receivable Aging", sw: "Uchakavu wa Madeni ya Wateja" },
  { en: "Accounts Payable Aging", sw: "Uchakavu wa Madeni ya Wauzaji" },
  { en: "Outstanding invoices grouped by the number of days overdue.", sw: "Ankara zinazodaiwa zilizopangwa kwa idadi ya siku zilizochelewa." },
  { en: "Counterparty", sw: "Mhusika Mwenzake" },
  { en: "Due Date", sw: "Tarehe ya Mwisho" },
  { en: "Days Overdue", sw: "Siku za Kuchelewa" },
  { en: "Aging Bucket", sw: "Kundi la Uchakavu" },
  { en: "Transaction Register", sw: "Orodha ya Miamala" },
  { en: "Journal lifecycle, source and approval trail.", sw: "Mzunguko wa jarida, chanzo na mkondo wa idhini." },
  { en: "Source", sw: "Chanzo" },
  { en: "Contribution Report", sw: "Ripoti ya Michango" },
  { en: "Collection and GL posting status for", sw: "Hali ya ukusanyaji na uwekaji wa Daftari Kuu kwa" },
  { en: "Month", sw: "Mwezi" },
  { en: "Records", sw: "Kumbukumbu" },
  { en: "Expected", sw: "Inayotarajiwa" },
  { en: "Collected", sw: "Iliyokusanywa" },
  { en: "Posted", sw: "Iliyowekwa" },
  { en: "Unassigned", sw: "Haijawekwa" },
  { en: "Finance Audit Report", sw: "Ripoti ya Ukaguzi wa Fedha" },
  { en: "Finance actions captured by the immutable audit trail.", sw: "Vitendo vya fedha vilivyorekodiwa na mkondo wa ukaguzi usiobadilika." },
  { en: "Timestamp", sw: "Muda" },
  { en: "Action", sw: "Kitendo" },
  { en: "Entity", sw: "Chombo" },
  { en: "Entity ID", sw: "Namba ya Chombo" },
  { en: "IP", sw: "IP" },

  // Table helper
  { en: "No records found.", sw: "Hakuna kumbukumbu zilizopatikana." },
  { en: "Page", sw: "Ukurasa" },
  { en: "of", sw: "wa" },
  { en: "total", sw: "jumla" },
  { en: "Previous", sw: "Iliyotangulia" },
  { en: "Next", sw: "Inayofuata" },

  // Cash form (receipts/payments)
  { en: "Cash/Bank account", sw: "Akaunti ya Fedha Taslimu/Benki" },
  { en: "Expense/account", sw: "Akaunti ya matumizi" },
  { en: "No budget commitment", sw: "Hakuna ahadi ya bajeti" },
  { en: "available", sw: "inayopatikana" },
  { en: "Create", sw: "Tengeneza" },
  { en: "receipt", sw: "risiti" },
  { en: "payment", sw: "malipo" },

  // Report title banner
  { en: "FINANCIAL REPORT", sw: "RIPOTI YA FEDHA" },

  // Finance documents panel
  { en: "SUPPORTING DOCUMENTS", sw: "NYARAKA ZA USAIDIZI" },
  { en: "Document title", sw: "Kichwa cha nyaraka" },
  { en: "PDF or Word document", sw: "Hati ya PDF au Word" },
  { en: "Upload", sw: "Pakia" },
  { en: "Title", sw: "Kichwa" },
  { en: "Size", sw: "Ukubwa" },
  { en: "Uploaded", sw: "Imepakiwa" },
  { en: "Download", sw: "Pakua" },

  // Invoice detail panel
  { en: "INVOICE DETAIL", sw: "MAELEZO YA ANKARA" },
  { en: "Print / PDF", sw: "Chapisha / PDF" },
  { en: "Invoice amount", sw: "Kiasi cha ankara" },
  { en: "Paid", sw: "Kilicholipwa" },
  { en: "Cash / bank ledger", sw: "Daftari la fedha taslimu / benki" },
  { en: "Payment reference", sw: "Kumbukumbu ya malipo" },
  { en: "Receive payment", sw: "Pokea malipo" },
  { en: "Record supplier payment", sw: "Rekodi malipo ya muuzaji" },
  { en: "Receipt allocation history", sw: "Historia ya ugawaji wa risiti" },
  { en: "Supplier payment history", sw: "Historia ya malipo ya muuzaji" },
  { en: "Approve & post", sw: "Idhinisha na chapisha" },
  { en: "Treasurer approval", sw: "Idhini ya mweka hazina" },
  { en: "Approval", sw: "Idhini" },
];

export default accountingDict;
